import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Mail, MapPin, Menu, Phone } from "lucide-react";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { CONTACT, DEALERSHIP_URL, LOGO_URL } from "@/constants/site";
import { NAVIGATION_GROUPS, TOP_LEVEL_NAVIGATION } from "@/constants/navigation";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { getLocaleFromPath, getLocalizedPath } from "@/lib/locale";

export const Navbar = ({ content = {}, alwaysVisible = false }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const contact = { ...CONTACT, ...(content.contact || {}) };
  const locale = getLocaleFromPath(location.pathname) || "tr";
  const navRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [showNavigation, setShowNavigation] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [openMobileGroup, setOpenMobileGroup] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
      setShowNavigation(window.scrollY > window.innerHeight * 0.65);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const closeNavigation = (event) => {
      if (event.key === "Escape") setOpenGroup(null);
      if (event.type === "pointerdown" && navRef.current && !navRef.current.contains(event.target)) setOpenGroup(null);
    };
    window.addEventListener("keydown", closeNavigation);
    window.addEventListener("pointerdown", closeNavigation);
    return () => {
      window.removeEventListener("keydown", closeNavigation);
      window.removeEventListener("pointerdown", closeNavigation);
    };
  }, []);

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setOpenMobileGroup(null);
  };
  const visible = alwaysVisible || showNavigation;
  const localPath = (path) => getLocalizedPath(path, locale);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[transform,opacity,background-color,border-color] duration-500 ${visible ? "translate-y-0 opacity-100" : "pointer-events-none -translate-y-full opacity-0"} ${scrolled || alwaysVisible ? "border-b border-detail/70 bg-surface/90 backdrop-blur-md" : "bg-transparent"}`}
    >
      <div data-testid="navbar-contact-bar" className="bg-ink text-cream">
        <div className="mx-auto flex h-9 max-w-7xl items-center px-5 text-xs sm:px-8">
          <span className="hidden items-center gap-2 lg:inline-flex"><MapPin className="h-3.5 w-3.5 text-brand" />{contact.address}</span>
          <div className="ml-auto flex items-center gap-4 sm:gap-6">
            <a href={contact.phone_href || contact.phoneHref} className="inline-flex items-center gap-2 transition-colors hover:text-brand"><Phone className="h-3.5 w-3.5 text-brand" />{contact.phone}</a>
            <a href={`mailto:${contact.email}`} className="hidden items-center gap-2 transition-colors hover:text-brand sm:inline-flex"><Mail className="h-3.5 w-3.5 text-brand" />{contact.email}</a>
          </div>
        </div>
      </div>

      <nav ref={navRef} className="relative mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to={localPath("/")} onClick={() => setOpenGroup(null)} data-testid="nav-logo" className="absolute left-1/2 -translate-x-1/2 lg:static lg:translate-x-0 flex shrink-0 items-center" aria-label={`Nolena Global ${t("nav.home")}`}>
          <img src={LOGO_URL} alt="Nolena Global" className="h-12 w-auto sm:h-14" />
        </Link>

        <ul className="hidden items-center gap-2 lg:flex xl:gap-3">
          <li><Link to={localPath("/")} className="rounded-full px-3 py-2 text-sm font-medium text-ink/80 transition hover:text-brand">{t(TOP_LEVEL_NAVIGATION[0].labelKey)}</Link></li>
          {NAVIGATION_GROUPS.map((group) => (
            <li key={group.id} className="relative">
              <button data-testid={`nav-group-${group.id}`} type="button" aria-expanded={openGroup === group.id} aria-haspopup="menu" onClick={() => setOpenGroup((current) => current === group.id ? null : group.id)} className={`inline-flex items-center gap-1 rounded-full px-3 py-2 text-sm font-medium transition ${openGroup === group.id ? "bg-detail/60 text-brand" : "text-ink/80 hover:text-brand"}`}>
                {t(group.labelKey)} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${openGroup === group.id ? "rotate-180" : ""}`} />
              </button>
              {openGroup === group.id && <div role="menu" className="absolute left-1/2 top-full mt-3 w-64 -translate-x-1/2 rounded-2xl border border-detail bg-cream p-2 shadow-[0_20px_50px_-28px_rgba(43,33,24,.42)]">
                {group.items.map((item) => <Link key={item.id} to={getLocalizedPath(item.path, locale)} onClick={() => setOpenGroup(null)} role="menuitem" className="block rounded-xl px-4 py-3 text-sm font-semibold text-ink/75 transition hover:bg-detail/45 hover:text-brand">{t(item.labelKey)}</Link>)}
              </div>}
            </li>
          ))}
          <li><Link to={localPath("/urun-kaynak-merkezi")} data-testid="nav-resource-center" className="ml-1 inline-flex rounded-full border border-brand/35 bg-brand/[.06] px-4 py-2 text-sm font-semibold text-brand transition hover:bg-brand hover:text-cream">{t(TOP_LEVEL_NAVIGATION[1].labelKey)}</Link></li>
        </ul>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a href={DEALERSHIP_URL} data-testid="nav-dealership-cta" className="hidden items-center rounded-full bg-brand px-5 py-2.5 text-sm font-semibold text-cream transition hover:bg-brand2 sm:inline-flex">{t("common.apply")}</a>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-trigger" aria-label={t("nav.openMenu")} className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-detail bg-surface/80 text-ink lg:hidden"><Menu className="h-5 w-5" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82vw] max-w-sm border-detail bg-cream">
              <SheetTitle className="sr-only">{t("nav.menu")}</SheetTitle>
              <div className="mb-8 mt-4"><img src={LOGO_URL} alt="Nolena Global" className="h-9 w-auto" /></div>
              <ul className="flex flex-col gap-1">
                <li><SheetClose asChild><Link to={localPath("/")} onClick={closeMobileMenu} className="block rounded-2xl px-4 py-3 text-lg font-medium text-ink transition hover:bg-detail/50">{t(TOP_LEVEL_NAVIGATION[0].labelKey)}</Link></SheetClose></li>
                {NAVIGATION_GROUPS.map((group) => <li key={group.id} data-testid={`mobile-nav-group-${group.id}`} className="rounded-2xl border border-detail/70">
                  <button type="button" aria-expanded={openMobileGroup === group.id} onClick={() => setOpenMobileGroup((current) => current === group.id ? null : group.id)} className="flex w-full items-center justify-between px-4 py-3 text-left text-lg font-medium text-ink"><span>{t(group.labelKey)}</span><ChevronDown className={`h-4 w-4 transition-transform ${openMobileGroup === group.id ? "rotate-180" : ""}`} /></button>
                  {openMobileGroup === group.id && <ul className="border-t border-detail/70 px-2 pb-2">{group.items.map((item) => <li key={item.id}><SheetClose asChild><Link to={getLocalizedPath(item.path, locale)} onClick={closeMobileMenu} className="block rounded-xl px-3 py-2.5 text-sm font-semibold text-ink/70 transition hover:bg-detail/45 hover:text-brand">{t(item.labelKey)}</Link></SheetClose></li>)}</ul>}
                </li>)}
                <li><SheetClose asChild><Link to={localPath("/urun-kaynak-merkezi")} onClick={closeMobileMenu} data-testid="mobile-nav-resource-center" className="mt-3 block rounded-2xl bg-brand px-4 py-3 text-lg font-semibold text-cream">{t(TOP_LEVEL_NAVIGATION[1].labelKey)}</Link></SheetClose></li>
              </ul>
              <a href={DEALERSHIP_URL} data-testid="mobile-dealership-cta" className="mt-8 inline-flex w-full items-center justify-center rounded-full border border-brand bg-brand/[.06] px-5 py-3.5 text-base font-semibold text-brand transition hover:bg-brand hover:text-cream">{t("common.apply")}</a>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
